import { useMemo } from 'react';
import { useGroupUserRoles, useUserRoles } from '@features/user';
import { ROLES } from '@utils/roleUtils';

import { payerRosterRowId } from '../components/shell/payerRosterIdentity';
import { useGetAdminIntakeHubDashboardQuery, useGetIntakeHubDashboardQuery } from '../store/api';
import type { IntakeHubDashboardResponse, IntakePayerRosterEntry } from '../types';

/** The requirement whose evidence carries the practice's payer roster. */
export const PAYER_ROSTER_REQUIREMENT_KEY = 'jotbill.payer_roster';

/**
 * One payer the practice marked "Wants to get credentialed" on the intake form.
 *
 * `key` is the roster's own identity (`payerRosterIdentity`), which is what the
 * credentialing matrix groups by. `stediId` and `payerId` are the only handles
 * the payer directory can be joined on, since credentialing records store a
 * directory UUID and the roster does not.
 */
export interface IntakeRosterPayer {
  key: string;
  payerId: string;
  payerName: string;
  stediId?: string | null;
}

export interface IntakePayerRosterResult {
  /** Roster payers with enrollment_status 'wants_to_enroll', in roster order. */
  payers: IntakeRosterPayer[];
  pending: boolean;
  failed: boolean;
  /**
   * True once a dashboard has been read and it carried a payer roster item.
   * False means the practice has no roster on file (or the read has not landed),
   * and the caller falls back to the record-only view rather than claiming the
   * practice asked for nothing.
   */
  available: boolean;
}

const EMPTY: IntakePayerRosterResult = {
  payers: [],
  pending: false,
  failed: false,
  available: false,
};

/**
 * The org-scope roster item. `user_id == null` is the roster's scope even when
 * the dashboard itself was requested for one provider — the same lookup
 * `IntakeExperience` uses to render the intake form's payer table.
 */
function rosterEntries(
  dashboard: IntakeHubDashboardResponse | undefined,
): readonly IntakePayerRosterEntry[] | undefined {
  const item = (dashboard?.items ?? []).find(
    entry => entry.requirement_key === PAYER_ROSTER_REQUIREMENT_KEY && entry.user_id == null,
  );
  return item?.evidence_summary?.payer_roster;
}

/**
 * The payers the practice asked to be credentialed for.
 *
 * `enrollment_status` is optional on the wire and the intake form treats a
 * missing value as "already enrolled" (see `normalizePayerRosterEntries`), so
 * the match is strict: only an explicit `wants_to_enroll` counts. Duplicate
 * identities collapse onto the first entry, exactly as the roster table keys.
 */
export function selectWantsCredentialingPayers(
  entries: readonly IntakePayerRosterEntry[] | undefined,
): IntakeRosterPayer[] {
  const seen = new Set<string>();
  const payers: IntakeRosterPayer[] = [];
  for (const entry of entries ?? []) {
    if (entry.enrollment_status !== 'wants_to_enroll') continue;
    const key = payerRosterRowId(entry);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    payers.push({
      key,
      payerId: entry.payer_id,
      payerName: entry.payer_name || entry.payer_id,
      stediId: entry.stedi_id ?? null,
    });
  }
  return payers;
}

function toResult(
  dashboard: IntakeHubDashboardResponse | undefined,
  pending: boolean,
  failed: boolean,
): IntakePayerRosterResult {
  const entries = rosterEntries(dashboard);
  return {
    payers: selectWantsCredentialingPayers(entries),
    pending,
    failed,
    available: entries !== undefined,
  };
}

/**
 * Staff read of the practice's roster.
 *
 * The arguments deliberately match `IntakeExperience`'s admin dashboard read
 * (`{ organizationId, userId, experience: 'v2' }`), so the staff credentialing
 * tab shares that cache entry instead of issuing a second fetch, and a roster
 * save that invalidates the dashboard refreshes both surfaces at once.
 */
export function useAdminIntakePayerRoster(
  organizationId: string,
  userId?: string,
): IntakePayerRosterResult {
  const { currentData, isFetching, isError } = useGetAdminIntakeHubDashboardQuery(
    { organizationId, userId, experience: 'v2' },
    { skip: !organizationId },
  );
  return useMemo(
    () => (organizationId ? toResult(currentData, isFetching, isError) : EMPTY),
    [organizationId, currentData, isFetching, isError],
  );
}

/**
 * Clinic-side read of the same roster.
 *
 * `view` repeats `IntakeExperience`'s derivation so the clinician's journey and
 * this panel share one cache entry. Practice administrators request the
 * all-provider scope; everyone else gets their own.
 */
export function useProviderIntakePayerRoster(enabled = true): IntakePayerRosterResult {
  const userRoles = useUserRoles();
  const groupUserRoles = useGroupUserRoles();
  const view =
    userRoles.includes(ROLES.PRACTICE_ADMINISTRATOR) ||
    groupUserRoles.includes(ROLES.PRACTICE_ADMINISTRATOR)
      ? 'all'
      : 'individual';
  const { currentData, isFetching, isError } = useGetIntakeHubDashboardQuery(
    { experience: 'v2', view },
    { skip: !enabled },
  );
  return useMemo(
    () => (enabled ? toResult(currentData, isFetching, isError) : EMPTY),
    [enabled, currentData, isFetching, isError],
  );
}
